// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// constant imports
import { CacheDuration } from './../../_constants/cache-duration';
import { URLConstants } from '../../_constants/url-constants';

// Model imports
import { WFEBaseParametersDto } from '../_models/wfe-base-parameter-dto';
import { WFESpecialHandlingResultDto } from '../_models/wfe-special-handling-result-dto';
import { WFEEditorParameterDto } from '../_models/wfe-editor-parameter-dto';
import { WorkflowChecklistEditorResultDto } from '../_models/workflow-check-list-editor-result-dto';
import { WorkflowCheckListParametersDto } from '../_models/workflow-check-list-parameters-dto';
import { VendorCodingDefaultEditorParameterDto } from '../_models/vendor-coding-default-editor-parameter-dto';
import { VendorCodingDefaultEditorResultDto } from '../_models/vendor-coding-default-editor-result-dto';
import { WFVendorCodingDefaultTemplateParametersDto } from '../_models/wf-vendor-coding-default-template-parameters-dto';
import { WFEWorkflowRawDto } from '../_models/wfe-workflow-raw-dto';
import { WFEVendorAccountEFTActiveStatusParameterDto } from '../_models/wfe-vendor-account-eft-active-status-parameter-dto';

@Injectable({
    providedIn: 'root'
})
export class WorkflowEditorSharedService {
    private readonly _subjectIsDefaultLineItemAvailable: Subject<boolean> = new Subject<boolean>();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getWorkflowSpecialHandling(baseParams: WFEBaseParametersDto): Observable<WFESpecialHandlingResultDto[]> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}specialhandlings${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WFESpecialHandlingResultDto[]>(url, options)
            .pipe(
                map((response: WFESpecialHandlingResultDto[]) => plainToClass(WFESpecialHandlingResultDto, response))
            );
    }

    getCodingDefaultEditor(
        baseParams: VendorCodingDefaultEditorParameterDto,
        workflowStep: string
    ): Observable<VendorCodingDefaultEditorResultDto> {
        const url = `${
            URLConstants.ccWorkflowEditorBaseApiUrl
        }${workflowStep}/coding-default/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get<VendorCodingDefaultEditorResultDto>(url)
            .pipe(
                map((response: VendorCodingDefaultEditorResultDto) =>
                    plainToClass(VendorCodingDefaultEditorResultDto, response)
                )
            );
    }

    saveCodingDefaultEditor(
        baseParams: WFVendorCodingDefaultTemplateParametersDto,
        workflowStep: string
    ): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}${workflowStep}/coding-default`,
            classToPlain(baseParams)
        );
    }

    isDefaultLineItemAvailable(): Observable<boolean> {
        return this._subjectIsDefaultLineItemAvailable;
    }

    updateLineItemAvailability(value: boolean): void {
        this._subjectIsDefaultLineItemAvailable.next(value);
    }

    getWorkflowChecklistEditor(
        baseParams: WFEEditorParameterDto,
        workflowStep: string
    ): Observable<WorkflowChecklistEditorResultDto> {
        const url = `${
            URLConstants.ccWorkflowEditorBaseApiUrl
        }${workflowStep}/checklist/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get<WorkflowChecklistEditorResultDto>(url)
            .pipe(map((response) => plainToClass(WorkflowChecklistEditorResultDto, response)));
    }

    saveWorkflowChecklist(baseParams: WorkflowCheckListParametersDto, workflowStep: string): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}${workflowStep}/checklist`,
            classToPlain(baseParams)
        );
    }

    getWorkflowAccountantChecklistEditor(
        baseParams: WFEEditorParameterDto
    ): Observable<WorkflowChecklistEditorResultDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}acct/checklist/editor${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<WorkflowChecklistEditorResultDto>(url, options)
            .pipe(map((response) => plainToClass(WorkflowChecklistEditorResultDto, response)));
    }

    saveWorkflowAccountantChecklist(baseParams: WorkflowCheckListParametersDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}acct/checklist`,
            classToPlain(baseParams)
        );
    }

    reclassBill(baseParams: WFEWorkflowRawDto, workflowStep: string): Observable<any> {
        return this.httpClient.put(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}${workflowStep}/reclass-bill`,
            classToPlain(baseParams)
        );
    }

    updateBill(baseParams: WFEWorkflowRawDto, workflowStep: string): Observable<any> {
        return this.httpClient.put(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}${workflowStep}/update-bill`,
            classToPlain(baseParams)
        );
    }

    updateVendorAccountEFT(baseParams: WFEVendorAccountEFTActiveStatusParameterDto): Observable<any> {
        return this.httpClient.put(
            `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor-account/eft`,
            classToPlain(baseParams)
        );
    }
}
